import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
} from '@mui/material'
import ContactDetail from './ContactDetail'
import MergeContactsModal from '../MergeContactsModal'
import ReassignIdentityModal from './ReassignIdentityModal'
import useContactDetailController from '../../hooks/useContactDetailController'
import useContactsDetailStore from '../../store/contactsDetail'
import useContactsListStore from '../../store/contactsList'

export interface ContactDetailHostProps {
  stationId: number
  contactId: number
  isAdmin: boolean
  onMergedRedirect: (toContactId: number) => void
  onDeletedRedirect?: () => void
}

const ContactDetailHost: React.FC<ContactDetailHostProps> = ({
  stationId,
  contactId,
  isAdmin,
  onMergedRedirect,
  onDeletedRedirect,
}) => {
  const removeContactFromList = useContactsListStore((state) => state.removeContact)
  const lastMergeEvent = useContactsDetailStore((state) => state.lastMergeEvent)
  const setLastMergeEvent = useContactsDetailStore((state) => state.setLastMergeEvent)

  const {
    detail,
    loading,
    error,
    saving,
    blocking,
    deleting,
    refresh,
    save,
    toggleContactBlock,
    toggleIdentityBlock,
    deleteContact,
    deleteDisabledReason,
  } = useContactDetailController({
    stationId,
    contactId,
    isAdmin,
    onDeleted: () => {
      onDeletedRedirect?.()
    },
  })

  const [mergeOpen, setMergeOpen] = React.useState(false)
  const [reassignIdentityId, setReassignIdentityId] = React.useState<number | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false)
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null)

  React.useEffect(() => {
    if (stationId && contactId) {
      void refresh()
    }
  }, [stationId, contactId, refresh])

  React.useEffect(() => {
    if (!lastMergeEvent) {
      return
    }
    if (lastMergeEvent.fromContactId !== contactId) {
      return
    }
    setLastMergeEvent(null)
    setSnackbar({ message: 'Contact merged', severity: 'success' })
    onMergedRedirect(lastMergeEvent.toContactId)
  }, [lastMergeEvent, contactId, setLastMergeEvent, onMergedRedirect])

  const showSnackbar = React.useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ message, severity })
  }, [])

  const handleSave = async (values: { name: string | null; location: string | null; notes: string | null }) => {
    try {
      await save(values)
      showSnackbar('Contact updated')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update contact.'
      showSnackbar(message, 'error')
    }
  }

  const handleToggleBlockContact = async (blocked: boolean) => {
    try {
      await toggleContactBlock(blocked)
      showSnackbar(blocked ? 'Contact blocked' : 'Contact unblocked')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update block status.'
      showSnackbar(message, 'error')
    }
  }

  const handleIdentityToggleBlock = async (identityId: number, blocked: boolean) => {
    try {
      await toggleIdentityBlock(identityId, blocked)
      showSnackbar(blocked ? 'Identity blocked' : 'Identity unblocked')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update identity.'
      showSnackbar(message, 'error')
    }
  }

  const handleIdentityReassign = (identityId: number) => {
    setReassignIdentityId(identityId)
  }

  const handleIdentityReassigned = async () => {
    await refresh()
    showSnackbar('Identity reassigned')
  }

  const handleMergeCompleted = (intoId: number) => {
    removeContactFromList(contactId)
    setMergeOpen(false)
    onMergedRedirect(intoId)
  }

  const handleDeleteRequest = () => {
    if (deleteDisabledReason) {
      showSnackbar(deleteDisabledReason, 'error')
      return
    }
    setConfirmDeleteOpen(true)
  }

  const handleDeleteConfirmed = async () => {
    const result = await deleteContact()
    if (result.ok) {
      showSnackbar('Contact deleted')
      setConfirmDeleteOpen(false)
      onDeletedRedirect?.()
    } else {
      showSnackbar(result.message ?? 'Unable to delete contact.', 'error')
    }
  }

  const handleRetry = () => {
    void refresh()
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={3}>
        {loading && !detail && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && !detail && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={handleRetry}>Retry</Button>}>
            {error}
          </Alert>
        )}
        {detail && (
          <ContactDetail
            contact={detail.contact}
            identities={detail.identities}
            mergeHistory={detail.merge_history}
            isAdmin={isAdmin}
            saving={saving}
            blocking={blocking}
            deleting={deleting}
            onSave={handleSave}
            onToggleBlockContact={handleToggleBlockContact}
            onMerge={() => setMergeOpen(true)}
            onDelete={handleDeleteRequest}
            onIdentityToggleBlock={handleIdentityToggleBlock}
            onIdentityReassign={handleIdentityReassign}
            deleteDisabledReason={deleteDisabledReason}
          />
        )}
      </Stack>
      {detail && (
        <>
          <MergeContactsModal
            open={mergeOpen}
            stationId={stationId}
            fromContactId={contactId}
            onClose={() => setMergeOpen(false)}
            onMerged={handleMergeCompleted}
          />
          <ReassignIdentityModal
            open={reassignIdentityId !== null}
            stationId={stationId}
            identityId={reassignIdentityId}
            onClose={() => setReassignIdentityId(null)}
            onReassigned={async () => {
              await handleIdentityReassigned()
              setReassignIdentityId(null)
            }}
          />
          <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
            <DialogTitle>Delete contact</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This action cannot be undone. Are you sure you want to delete this contact?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDeleteOpen(false)} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirmed}
                color="error"
                variant="contained"
                disabled={deleting}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      <Snackbar open={Boolean(snackbar)} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        {snackbar ? (
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default ContactDetailHost
